import { GoogleOAuthProvider } from "@react-oauth/google";
import { GlobalStyles } from "@mui/material";
import MyAdmin from "./admin";
import { ThemeModeProvider } from "./context/ThemeModeContext";
import { PermissionProvider } from "./context/PermissionContext";
import { SocketIOProvider } from "./providers/SocketIOProvider";
import { OrderNotificationProvider } from "./providers/OrderNotificationProvider";
import { CometChatProvider } from "./providers/CometChatProvider";
import { Sentry } from "./utils/sentry";
import config from "./config";
import "./styles.css";

/**
 * Global notification styles
 *
 * These are applied via MUI GlobalStyles to ensure they override
 * portal-rendered Snackbar components (which render at the body level).
 * !important is required because MUI's emotion styles have high specificity.
 */
const notificationStyles = {
  // Target all Snackbar Alert variants - force white text
  '.MuiSnackbar-root .MuiAlert-root': {
    color: '#FFFFFF !important',
  },
  '.MuiSnackbar-root .MuiAlert-root .MuiAlert-message': {
    color: '#FFFFFF !important',
  },
  '.MuiSnackbar-root .MuiAlert-root .MuiAlert-icon': {
    color: '#FFFFFF !important',
  },
  '.MuiSnackbar-root .MuiAlert-root .MuiAlert-icon svg': {
    color: '#FFFFFF !important',
    fill: '#FFFFFF !important',
  },
  '.MuiSnackbar-root .MuiAlert-root .MuiAlert-action': {
    color: '#FFFFFF !important',
  },
  // Error notifications
  '.MuiSnackbar-root .MuiAlert-filledError, .MuiSnackbar-root .MuiAlert-standardError': {
    backgroundColor: '#D32F2F !important',
    color: '#FFFFFF !important',
  },
  // Warning notifications
  '.MuiSnackbar-root .MuiAlert-filledWarning, .MuiSnackbar-root .MuiAlert-standardWarning': {
    backgroundColor: '#ED6C02 !important',
    color: '#FFFFFF !important',
  },
  // Success notifications
  '.MuiSnackbar-root .MuiAlert-filledSuccess, .MuiSnackbar-root .MuiAlert-standardSuccess': {
    backgroundColor: '#2E7D32 !important',
    color: '#FFFFFF !important',
  },
  // Info notifications
  '.MuiSnackbar-root .MuiAlert-filledInfo, .MuiSnackbar-root .MuiAlert-standardInfo': {
    backgroundColor: '#0288D1 !important',
    color: '#FFFFFF !important',
  },
  // Snackbar content (if used directly)
  '.MuiSnackbar-root .MuiSnackbarContent-root': {
    backgroundColor: '#323232 !important',
    color: '#FFFFFF !important',
  },
  '.MuiSnackbar-root .MuiSnackbarContent-message': {
    color: '#FFFFFF !important',
  },
};

const SentryFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <p>Something went wrong. Please refresh the page.</p>
  </div>
);

const App = () => (
  <Sentry.ErrorBoundary fallback={SentryFallback}>
    <GoogleOAuthProvider clientId={config.GOOGLE_CLIENT_ID}>
      <GlobalStyles styles={notificationStyles} />
      <ThemeModeProvider>
        <PermissionProvider>
          <SocketIOProvider>
            <OrderNotificationProvider>
              <CometChatProvider>
                <MyAdmin />
              </CometChatProvider>
            </OrderNotificationProvider>
          </SocketIOProvider>
        </PermissionProvider>
      </ThemeModeProvider>
    </GoogleOAuthProvider>
  </Sentry.ErrorBoundary>
);

export default App;
